// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminStatistics\AdminStatistics.js
// Версия: 3.0.0 - Strategy 2026
// Дата: 26.01.2026

import React, { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import './AdminStatistics.css';

const PERIODS = [
  { id: 'today', label: 'Сегодня' },
  { id: 'week', label: '7 дней' },
  { id: 'month', label: '30 дней' },
  { id: 'all', label: 'Всё время' },
];

const AdminStatistics = () => {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [period]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/stats?period=${period}`);
      if (res.success) {
        setStats(res.data);
      }
    } catch (error) {
      console.error('Stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return parseInt(num).toLocaleString('ru-RU');
  };

  const formatMoney = (num) => {
    if (!num) return '€0';
    return '€' + parseFloat(num).toLocaleString('ru-RU', { minimumFractionDigits: 0 });
  };

  if (loading && !stats) {
    return <div className="stats-loading">📊 Загрузка статистики...</div>;
  }

  const users = stats?.users || {};
  const subscriptions = stats?.subscriptions || {};
  const campaigns = stats?.campaigns || {};
  const leads = stats?.leads || {};
  const wallets = stats?.wallets || {};
  const revenue = stats?.revenue || {};
  const promocodes = stats?.promocodes || {};

  return (
    <div className="admin-statistics">
      {/* Header */}
      <div className="stats-header">
        <div>
          <h1 className="stats-title">📊 Аналитика DARY</h1>
          <p className="stats-subtitle">Strategy 2026 • Real-time</p>
        </div>
        <div className="stats-period-buttons">
          {PERIODS.map(p => (
            <button
              key={p.id}
              className={`stats-period-btn ${period === p.id ? 'active' : ''}`}
              onClick={() => setPeriod(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="stats-main-grid">
        <div className="stats-card primary">
          <div className="stats-card-icon">💰</div>
          <div className="stats-card-content">
            <span className="stats-card-value">{formatMoney(revenue.total)}</span>
            <span className="stats-card-label">Общий доход</span>
          </div>
          <div className="stats-card-sub">Direct: {formatMoney(revenue.direct)} • Referral: {formatMoney(revenue.referral)}</div>
        </div>

        <div className="stats-card">
          <div className="stats-card-icon">👥</div>
          <div className="stats-card-content">
            <span className="stats-card-value">{formatNumber(users.total)}</span>
            <span className="stats-card-label">Пользователей</span>
          </div>
          <div className="stats-card-sub">{formatNumber(users.businesses)} бизнесов</div>
        </div>

        <div className="stats-card">
          <div className="stats-card-icon">🎯</div>
          <div className="stats-card-content">
            <span className="stats-card-value">{formatNumber(leads.total)}</span>
            <span className="stats-card-label">Лидов</span>
          </div>
          <div className="stats-card-sub">{formatNumber(leads.direct)} direct • {formatNumber(leads.referral)} referral</div>
        </div>

        <div className="stats-card">
          <div className="stats-card-icon">💳</div>
          <div className="stats-card-content">
            <span className="stats-card-value">{formatMoney(wallets.total_deposit)}</span>
            <span className="stats-card-label">Brand Wallets</span>
          </div>
          <div className="stats-card-sub">{formatMoney(wallets.total_available)} available</div>
        </div>
      </div>

      {/* Detailed Sections */}
      <div className="stats-sections">
        {/* Subscriptions Section */}
        <div className="stats-section">
          <h3 className="stats-section-title">💎 Подписки</h3>
          <div className="stats-section-grid">
            <div className="stats-item success">
              <span className="stats-item-icon">✅</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatNumber(subscriptions.active)}</span>
                <span className="stats-item-label">Активных</span>
              </div>
            </div>
            <div className="stats-item">
              <span className="stats-item-icon">🆓</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatNumber(subscriptions.trial)}</span>
                <span className="stats-item-label">Триал</span>
              </div>
            </div>
            <div className="stats-item warning">
              <span className="stats-item-icon">⏳</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatNumber(subscriptions.expired)}</span>
                <span className="stats-item-label">Истекло</span>
              </div>
            </div>
            <div className="stats-item gold">
              <span className="stats-item-icon">💵</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatMoney(subscriptions.mrr)}</span>
                <span className="stats-item-label">MRR</span>
              </div>
            </div>
          </div>
        </div>

        {/* Campaigns Section */}
        <div className="stats-section">
          <h3 className="stats-section-title">📋 Кампании</h3>
          <div className="stats-section-grid">
            <div className="stats-item">
              <span className="stats-item-icon">📊</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatNumber(campaigns.total)}</span>
                <span className="stats-item-label">Всего</span>
              </div>
            </div>
            <div className="stats-item success">
              <span className="stats-item-icon">🟢</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatNumber(campaigns.active)}</span>
                <span className="stats-item-label">Активных</span>
              </div>
            </div>
            <div className="stats-item">
              <span className="stats-item-icon">🎁</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatNumber(campaigns.gifts)}</span>
                <span className="stats-item-label">AI Gifts</span>
              </div>
            </div>
            <div className="stats-item">
              <span className="stats-item-icon">✅</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatNumber(campaigns.claimed)}</span>
                <span className="stats-item-label">Claimed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Promocodes Section */}
        <div className="stats-section">
          <h3 className="stats-section-title">🎟️ Промокоды</h3>
          <div className="stats-section-grid">
            <div className="stats-item">
              <span className="stats-item-icon">📊</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatNumber(promocodes.total)}</span>
                <span className="stats-item-label">Всего</span>
              </div>
            </div>
            <div className="stats-item">
              <span className="stats-item-icon">🎁</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatNumber(promocodes.main)}</span>
                <span className="stats-item-label">Main (€7)</span>
              </div>
            </div>
            <div className="stats-item">
              <span className="stats-item-icon">👥</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatNumber(promocodes.referral)}</span>
                <span className="stats-item-label">Referral (€5)</span>
              </div>
            </div>
            <div className="stats-item success">
              <span className="stats-item-icon">✅</span>
              <div className="stats-item-info">
                <span className="stats-item-value">{formatNumber(promocodes.redeemed)}</span>
                <span className="stats-item-label">Redeemed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="stats-section">
          <h3 className="stats-section-title">⚡ Быстрые действия</h3>
          <div className="stats-actions">
            <a href="/admin/subscriptions" className="stats-action-btn">💎 Подписки</a>
            <a href="/admin/wallets" className="stats-action-btn">💳 Wallets</a>
            <a href="/admin/leads" className="stats-action-btn">🎯 Лиды</a>
            <a href="/admin/commissions" className="stats-action-btn">💵 Комиссии</a>
            <a href="/admin/campaigns" className="stats-action-btn">📋 Кампании</a>
            <a href="/admin/promocodes" className="stats-action-btn">🎟️ Промокоды</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStatistics;